// Company-specific payment wording for customer emails (quote, organiser and invitee login emails).

// Days before the event the balance is due when a company has no Cutoff For Payment set
// (same default as ggb_api LeadService::DEFAULT_PAY_DATE_DAYS)
const DEFAULT_PAY_DATE_DAYS = 42

// The company's currency symbol, e.g. "€" for Stagit, "£" for Freedom
export function currencyFor(company) {
    return (company && company.currency) || '£'
}

// Days invitees have to pay their deposit, counted from the organiser's first payment
const INVITEE_DEPOSIT_DAYS = 21

const formatDate = (date) => {
    const pad = (n) => String(n).padStart(2, '0')
    return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`
}

// "YYYY-MM-DD..." → local Date for that day (avoids the UTC shift of new Date('YYYY-MM-DD'))
const toDay = (value) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || ''))
    return m ? new Date(+m[1], +m[2] - 1, +m[3]) : null
}

// The "Deposit Deadline" / "Final Payment Deadline" lines under the Terms link in the organiser
// and invitee emails, as DD/MM/YYYY. The deposit deadline is the client portal's
// (ggb_client_app deposit_window.ts): 21 days from the organiser's first payment (today if
// there's none yet) plus the event's cutoff extension, and never after the pay date, when the
// deposit window closes anyway. Matches ggb_api LeadService::paymentDeadlinesHtml().
export function paymentDeadlinesHtml(event) {
    const payDate = toDay(event && event.pay_date)
    const payments = (event && event.lead && event.lead.payments) || []
    const firstPayment = payments.find(p => p.payment_date)
    const now = new Date()
    const deposit = (firstPayment && toDay(firstPayment.payment_date))
        || new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const extension = parseInt(event && event.cutoff_extension) || 0
    deposit.setDate(deposit.getDate() + INVITEE_DEPOSIT_DAYS + extension)
    const depositDeadline = payDate && payDate < deposit ? payDate : deposit

    let html = `<br><br><b>Deposit Deadline: ${formatDate(depositDeadline)}</b>`
    if (payDate) html += `<br><b>Final Payment Deadline: ${formatDate(payDate)}</b>`
    return html
}

// The "Minimum Group Size" line(s) in the organiser booking confirmation email, one per booked
// package that has a minimum (quotes.min_groupsize, set in the Custom Quote editor). The package
// name is added when the event has more than one booked package. '' when none has a minimum.
// See docs/reference/MINIMUM_GROUP_SIZE.md.
export function minimumGroupSizeHtml(event) {
    const quotes = (event && event.quotes) || []
    const withMinimum = quotes.filter(q => Number(q.min_groupsize) > 0)
    if (withMinimum.length === 0) return ''
    const escape = (text) => String(text || '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]))
    const lines = withMinimum.map(q => {
        const label = quotes.length > 1 ? `Minimum Group Size (${escape(q.name)})` : 'Minimum Group Size'
        const n = Number(q.min_groupsize)
        return `<b>${label}: ${n} ${n === 1 ? 'person' : 'people'}</b>`
    })
    return `<br><br>${lines.join('<br>')}<br>Your package is priced for at least this many people paying by the Final Payment Deadline.`
}

// Stagit only (incl. its Henit hen parties): the accommodation security deposit note under the
// deadlines in the organiser and invitee emails. Matches ggb_api LeadService::securityDepositHtml().
export function securityDepositHtml(company) {
    const name = String((company && company.company_name) || '').trim().toLowerCase()
    if (name !== 'stagit') return ''
    return '<br><br>Your accommodation may require a <b>fully refundable security deposit</b>. This is a standard good-behaviour bond between your group and the accommodation provider.'
}

// How long before the event the balance is due, from the company's Cutoff For Payment
// (cutoff_duration, in days): "4 weeks" for Stagit (28), "6 weeks" for Freedom (42), "30 days"
// when it isn't whole weeks. Matches ggb_api LeadService::balanceDueLabel().
export function balanceDueLabel(company) {
    const cutoff = Number(company && company.cutoff_duration)
    const days = cutoff > 0 ? Math.floor(cutoff) : DEFAULT_PAY_DATE_DAYS
    return days % 7 === 0 ? `${days / 7} weeks` : `${days} days`
}
